import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import AuthImg from "./AuthImg";
import {
  ClipboardCheck, PenLine, ArrowUpRight, X, FileText, Download, Eye, Send, Loader2, Ban,
} from "lucide-react";

const EDL_STATUS = {
  draft: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  signed_client: { label: "Signé (client)", cls: "bg-emerald-100 text-emerald-700" },
  signed_vendor: { label: "Signé (loueur)", cls: "bg-blue-100 text-blue-700" },
  signed_both: { label: "Signé", cls: "bg-emerald-100 text-emerald-700" },
  refused_by_client: { label: "Refus client documenté", cls: "bg-red-100 text-red-700" },
};

const EMAIL_STATUS = {
  simulated: { label: "Email simulé", cls: "bg-amber-100 text-amber-700" },
  sent: { label: "Email envoyé", cls: "bg-emerald-100 text-emerald-700" },
  failed: { label: "Envoi échoué", cls: "bg-red-100 text-red-700" },
  invalid_recipient: { label: "Email client invalide", cls: "bg-red-100 text-red-700" },
  pending: { label: "En attente", cls: "bg-slate-100 text-slate-600" },
};

const PHASE_OF = { contract: "departure", edl_departure: "departure", edl_return: "return", return_statement: "return" };

async function openPdf(path, download = false, filename = "document.pdf") {
  try {
    const r = await api.get(`/files/${path}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    if (download) {
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
    } else {
      window.open(url, "_blank");
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch {
    toast.error("Impossible d'ouvrir le document");
  }
}

export function BookingProofs({ bookingId }) {
  const [inspections, setInspections] = useState({});
  const [docsData, setDocsData] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [sending, setSending] = useState(null);

  const loadDocs = () =>
    api.get(`/vendor/bookings/${bookingId}/documents`).then(({ data }) => setDocsData(data)).catch(() => {});

  useEffect(() => {
    ["departure", "return"].forEach((type) => {
      api.get(`/inspections/${bookingId}/${type}`)
        .then(({ data }) => setInspections((s) => ({ ...s, [type]: data.inspection })))
        .catch(() => {});
    });
    loadDocs();
  }, [bookingId]); // eslint-disable-line

  const resend = async (phase) => {
    setSending(phase);
    try {
      const { data } = await api.post(`/vendor/bookings/${bookingId}/documents/send?phase=${phase}`);
      toast.success(`Envoi effectué — statut : ${(EMAIL_STATUS[data.email?.status] || {}).label || data.email?.status}`);
      loadDocs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally { setSending(null); }
  };

  const entries = ["departure", "return"].filter((t) => inspections[t]);
  const documents = docsData?.documents || [];
  const emails = docsData?.emails || [];
  if (entries.length === 0 && documents.length === 0) return null;

  const docsByPhase = { departure: documents.filter((d) => PHASE_OF[d.doc_type] === "departure" && d.latest), return: documents.filter((d) => PHASE_OF[d.doc_type] === "return" && d.latest) };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="section-proofs">
      <div className="overline mb-4">Preuves & documents</div>

      {/* ─── PDF figés par phase ─── */}
      {["departure", "return"].map((phase) => {
        const docs = docsByPhase[phase];
        if (!docs.length) return null;
        const emailInfo = phase === "departure" ? docsData?.departure_docs_email : docsData?.return_docs_email;
        const es = EMAIL_STATUS[emailInfo?.status] || null;
        return (
          <div key={phase} className="rounded-xl border border-slate-100 p-4 mb-4" data-testid={`docs-phase-${phase}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-violet-600" />
                <span className="text-sm font-semibold text-slate-900">Documents PDF — {phase === "departure" ? "Départ" : "Retour"}</span>
                {es && <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${es.cls}`} data-testid={`docs-email-badge-${phase}`}>{es.label}{emailInfo?.attempts > 1 ? ` (${emailInfo.attempts} tentatives)` : ""}</span>}
              </div>
              <button onClick={() => resend(phase)} disabled={sending === phase}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-700 hover:border-violet-300 disabled:opacity-50"
                data-testid={`docs-resend-${phase}`}>
                {sending === phase ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                {emailInfo?.status ? "Renvoyer au client" : "Envoyer au client"}
              </button>
            </div>
            <div className="space-y-1.5">
              {docs.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2.5" data-testid={`doc-row-${d.doc_type}`}>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-900 truncate">{d.label}</div>
                    <div className="text-[10px] text-slate-500">
                      {d.reference || "—"} · v{d.version} · {new Date(d.generated_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {(d.size_bytes / 1024).toFixed(0)} Ko
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => openPdf(d.file_path, false)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:border-violet-300" title="Consulter" data-testid={`doc-view-${d.doc_type}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => openPdf(d.file_path, true, `${d.doc_type}-${d.reference || d.version}.pdf`)} className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:border-violet-300" title="Télécharger" data-testid={`doc-download-${d.doc_type}`}>
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* ─── EDL & photos ─── */}
      <div className="space-y-4">
        {entries.map((type) => {
          const insp = inspections[type];
          const st = insp.client_refusal ? EDL_STATUS.refused_by_client : (EDL_STATUS[insp.status] || EDL_STATUS.draft);
          const photos = (insp.zones || []).flatMap((z) => (z.photos || []).map((p) => ({ path: p, zone: z.name })));
          const damaged = (insp.zones || []).filter((z) => z.damages);
          return (
            <div key={type} className="rounded-xl border border-slate-100 p-4" data-testid={`proofs-edl-${type}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <ClipboardCheck className="h-4 w-4 text-violet-600" />
                  <span className="text-sm font-semibold text-slate-900">
                    EDL {type === "departure" ? "départ" : "retour"} {insp.reference ? <span className="font-mono text-xs text-slate-400">· {insp.reference}</span> : null}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${st.cls}`} data-testid={`proofs-edl-status-${type}`}>{st.label}</span>
                </div>
                <Link to={`/inspection/${bookingId}/${type}`} className="inline-flex items-center gap-1 text-xs font-bold text-violet-600 hover:underline" data-testid={`proofs-edl-open-${type}`}>
                  Ouvrir <ArrowUpRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-2">
                <span>Km : <span className="font-semibold text-slate-800">{insp.mileage != null ? insp.mileage.toLocaleString("fr-FR") : "—"}</span></span>
                {insp.battery_level != null
                  ? <span>Batterie : <span className="font-semibold text-slate-800">{insp.battery_level} %</span></span>
                  : <span>Carburant : <span className="font-semibold text-slate-800">{insp.fuel_level || "—"}</span></span>}
                <span>Dommages : <span className="font-semibold text-slate-800">{damaged.length} zone{damaged.length !== 1 ? "s" : ""}</span></span>
                {insp.client_signature && (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold">
                    <PenLine className="h-3 w-3" /> Signé par {insp.client_signature.signer_name}{insp.client_signature.reserve ? " (avec réserve)" : ""}
                  </span>
                )}
                {insp.client_refusal && (
                  <span className="inline-flex items-center gap-1 text-red-600 font-semibold">
                    <Ban className="h-3 w-3" /> Refus documenté
                  </span>
                )}
              </div>
              {insp.client_signature?.reserve && (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-2" data-testid={`proofs-reserve-${type}`}>
                  Réserve du client : « {insp.client_signature.reserve} »
                </div>
              )}
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid={`proofs-photos-${type}`}>
                  {photos.map((p, i) => (
                    <AuthImg key={p.path} path={p.path} alt={p.zone}
                      className="h-14 w-14 rounded-lg object-cover border border-slate-200 cursor-pointer"
                      onClick={() => setLightbox(p)} data-testid={`proofs-photo-${type}-${i}`} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Historique d'envoi ─── */}
      {emails.length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-100 p-4" data-testid="email-history">
          <div className="text-xs font-bold text-slate-500 mb-2">Historique d'envoi</div>
          <div className="space-y-1">
            {emails.map((e) => {
              const es = EMAIL_STATUS[e.status] || { label: e.status, cls: "bg-slate-100 text-slate-600" };
              return (
                <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 text-[11px] py-1.5 border-b border-slate-50 last:border-0">
                  <span className="text-slate-700 truncate">{e.template_key === "departure_documents" ? "Documents de départ" : "Documents de restitution"} → {e.to} {(e.attachments || []).length ? `(${e.attachments.length} PJ)` : ""}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="text-slate-400">{new Date(e.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                    <span className={`px-2 py-0.5 rounded-full font-bold ${es.cls}`}>{es.label}</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="proofs-lightbox">
          <AuthImg path={lightbox.path} className="max-h-[80vh] max-w-full rounded-xl object-contain" />
          <div className="text-white text-xs font-semibold mt-2">{lightbox.zone}</div>
          <button className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 text-white flex items-center justify-center">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}

export default BookingProofs;
